
import { useState } from "react";
import { useTheme } from "@/components/ThemeProvider";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { SunIcon, MoonIcon, MonitorIcon } from "lucide-react";
import { toast } from "sonner";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguagePath } from "@/utils/language";

export const AppearanceSettings = () => {
  const { theme, setTheme } = useTheme();
  const [activeTheme, setActiveTheme] = useState(theme);
  const { t } = useTranslations("settings");
  const { getPath } = useLanguagePath();

  const handleThemeChange = (newTheme: 'light' | 'dark' | 'system') => {
    setActiveTheme(newTheme);
    setTheme(newTheme);
    toast.success(`Tema alterado para ${
      newTheme === 'light' ? 'claro' : 
      newTheme === 'dark' ? 'escuro' : 'sistema'
    }`);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href={getPath("/dashboard")} className="text-flame hover:text-flame/80">
                {t("breadcrumbs.dashboard")}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href={getPath("/dashboard/settings")} className="text-flame hover:text-flame/80">
                {t("breadcrumbs.settings")}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="font-medium dark:text-white">{t("breadcrumbs.appearance")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      
      <Card className="border-slate-100/50 dark:border-gray-700/50">
        <CardHeader className="bg-gray-50/50 dark:bg-gray-900/50">
          <CardTitle className="text-foreground dark:text-white">{t("appearance.title")}</CardTitle>
          <CardDescription className="text-muted-foreground dark:text-gray-400">
            {t("appearance.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 pt-6 bg-gray-50/50 dark:bg-gray-900/50">
          <div className="space-y-2">
            <Label className="text-foreground dark:text-gray-300">{t("appearance.theme")}</Label>
            <div className="grid grid-cols-3 gap-4">
              <Button 
                variant={activeTheme === 'light' ? "default" : "outline"}
                className={`flex flex-col items-center justify-between gap-2 h-auto p-4 ${
                  activeTheme !== 'light' ? 'dark:border-gray-800 dark:text-gray-300' : ''
                }`}
                onClick={() => handleThemeChange('light')}
              >
                <SunIcon className="h-6 w-6" />
                <span>{t("appearance.light")}</span>
              </Button>
              <Button 
                variant={activeTheme === 'dark' ? "default" : "outline"}
                className={`flex flex-col items-center justify-between gap-2 h-auto p-4 ${
                  activeTheme !== 'dark' ? 'dark:border-gray-800 dark:text-gray-300' : ''
                }`}
                onClick={() => handleThemeChange('dark')}
              >
                <MoonIcon className="h-6 w-6" />
                <span>{t("appearance.dark")}</span>
              </Button>
              <Button 
                variant={activeTheme === 'system' ? "default" : "outline"}
                className={`flex flex-col items-center justify-between gap-2 h-auto p-4 ${
                  activeTheme !== 'system' ? 'dark:border-gray-800 dark:text-gray-300' : ''
                }`}
                onClick={() => handleThemeChange('system')}
              >
                <MonitorIcon className="h-6 w-6" />
                <span>{t("appearance.system")}</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
